// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// Answers: what does it cost right now near me, and is tomorrow cheaper?
// Every row is clickable and opens the station drawer.

import { loadMeta, loadSites, prefs } from '../data';
import type { Meta, Site } from '../types';
import { PRODUCT_NAMES, cents, deltaLabel, esc, escTip, priceColour, shortDate, tankSaving } from '../format';
import { glossaryTerm } from '../glossary';
import { openStation } from '../components/drawer';

export async function renderToday(root: HTMLElement): Promise<void> {
  root.innerHTML = '<div class="skeleton"></div>';
  const [{ sites }, meta] = await Promise.all([loadSites(), loadMeta()]);

  let product = prefs.get('todayProduct', 'ULP');
  let state = prefs.get('todayState', 'WA');
  let query = '';
  let staleCount = 0;

  const products = [...new Set(sites.flatMap((s) => Object.keys(s.p)))]
    .filter((p) => sites.filter((s) => s.p[p]).length > 40)
    .sort((a, b) => sites.filter((s) => s.p[b]).length - sites.filter((s) => s.p[a]).length);

  root.innerHTML = `
    <div class="view-head">
      <h1>What it costs right now</h1>
      <p>The most recent price reported by every station. In Western Australia you can also see
      ${glossaryTerm('tomorrow', "tomorrow's price")} — the only place in Australia where it is published in advance.</p>
    </div>
    <div id="today-summary"></div>
    <div class="panel">
      <div class="controls">
        <div class="seg" id="today-state" role="group" aria-label="State">
          <button type="button" data-state="WA">Western Australia</button>
          <button type="button" data-state="QLD">Queensland</button>
        </div>
        <label for="today-product">Fuel</label>
        <select id="today-product">${products.map((p) => `<option value="${esc(p)}">${esc(PRODUCT_NAMES[p] ?? p)}</option>`).join('')}</select>
        <input type="search" id="today-search" placeholder="Suburb or station…" aria-label="Search suburb or station" />
      </div>
      <div id="today-table"></div>
    </div>
  `;

  const stateSeg = root.querySelector('#today-state') as HTMLElement;
  const productSel = root.querySelector('#today-product') as HTMLSelectElement;
  const search = root.querySelector('#today-search') as HTMLInputElement;
  if (products.includes(product)) productSel.value = product; else product = products[0];

  const syncSeg = () => stateSeg.querySelectorAll('button').forEach((b) => {
    b.setAttribute('aria-pressed', String((b as HTMLElement).dataset.state === state));
  });
  stateSeg.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('button') as HTMLElement | null;
    if (!btn) return;
    state = btn.dataset.state ?? 'WA';
    prefs.set('todayState', state);
    syncSeg();
    draw();
  });
  productSel.addEventListener('change', () => { product = productSel.value; prefs.set('todayProduct', product); draw(); });

  let timer = 0;
  search.addEventListener('input', () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => { query = search.value.trim().toLowerCase(); draw(); }, 300);
  });

  syncSeg();

  /**
   * "Today" must mean today. A station that last reported two months ago is
   * closed or has dropped out of the scheme, and its old price would otherwise
   * top the cheapest-first ranking and be read as a live offer. Stations are
   * kept only if they reported within a fortnight of that state's most recent
   * data; the rest still appear in Suburbs, which is explicitly a four-month
   * window, and the count of excluded stations is stated below the table.
   */
  const FRESH_DAYS = 14;
  const freshFrom = (st: string): string => {
    const latest = meta.latest[st];
    if (!latest) return '0000-00-00';
    return new Date(Date.parse(`${latest}T00:00:00Z`) - FRESH_DAYS * 86400000).toISOString().slice(0, 10);
  };

  function draw(): void {
    const cutoff = freshFrom(state);
    const stale = sites.filter((s) => s.st === state && s.p[product] && s.d < cutoff).length;
    const all = sites.filter((s) => s.st === state && s.p[product] && s.d >= cutoff);
    staleCount = stale;
    const shown = query
      ? all.filter((s) => s.s.toLowerCase().includes(query) || s.n.toLowerCase().includes(query) || s.pc.includes(query))
      : all;
    renderSummary(root.querySelector('#today-summary')!, all, product, state, meta);
    renderTable(root.querySelector('#today-table')!, shown, product, all.length, staleCount);
  }
  draw();
}

function renderSummary(host: HTMLElement, sites: Site[], product: string, state: string, meta: Meta): void {
  if (!sites.length) { host.innerHTML = ''; return; }
  const prices = sites.map((s) => s.p[product]).sort((a, b) => a - b);
  const mid = prices[Math.floor(prices.length / 2)];
  const cheapest = sites.reduce((a, b) => (b.p[product] < a.p[product] ? b : a));
  const dearest = sites.reduce((a, b) => (b.p[product] > a.p[product] ? b : a));
  // The realistic saving, not the theoretical one. The full range is set by a
  // single remote roadhouse hundreds of kilometres from anywhere, so "$155 a
  // tank" is true and useless; the 10th-to-90th percentile is the money
  // actually on the table for a driver choosing between reachable stations.
  const p10 = prices[Math.floor(prices.length * 0.1)];
  const p90 = prices[Math.floor(prices.length * 0.9)];
  const withTomorrow = sites.filter((s) => s.t && s.t[product] != null);
  const tomorrowShift = withTomorrow.length
    ? withTomorrow.reduce((a, s) => a + (s.t![product] - s.p[product]), 0) / withTomorrow.length
    : null;

  host.innerHTML = `
    <div class="grid-cards" style="margin-bottom:var(--space-lg)">
      <div class="stat-card"><b>${cents(mid)}c</b><span>middle of the market · ${sites.length} stations</span></div>
      <div class="stat-card"><b>${cents(cheapest.p[product])}c</b><span class="truncate">cheapest — ${esc(cheapest.s)}</span></div>
      <div class="stat-card"><b>${cents(dearest.p[product])}c</b><span class="truncate">dearest — ${esc(dearest.s)}</span></div>
      <div class="stat-card"><b>${tankSaving(p90 - p10)}</b><span>a tank, between a cheap and a dear station</span></div>
      ${tomorrowShift != null ? `<div class="stat-card"><b>${deltaLabel(tomorrowShift)}</b><span>average change tomorrow · ${withTomorrow.length} stations</span></div>` : ''}
    </div>
    <p class="brand-sub" style="margin:-8px 0 var(--space-lg)">
      Latest ${esc(state === 'WA' ? 'Western Australian' : 'Queensland')} data: ${esc(shortDate(meta.latest[state] ?? ''))}.
      ${state === 'QLD' ? 'Queensland stations report only when their price changes, so a station\'s figure is its last reported price.' : 'Western Australia publishes every station every day.'}
    </p>
  `;
}

function renderTable(host: HTMLElement, sites: Site[], product: string, total: number, stale: number): void {
  if (!sites.length) {
    host.innerHTML = '<p class="empty">No stations match that search.</p>';
    return;
  }
  const sorted = [...sites].sort((a, b) => a.p[product] - b.p[product]);
  const prices = sorted.map((s) => s.p[product]);
  const lo = prices[0];
  const hi = prices[prices.length - 1];
  const rows = sorted.slice(0, 300);

  host.innerHTML = `
    <div class="table-scroll">
      <table>
        <thead><tr>
          <th class="n">#</th><th>Station</th><th>Suburb</th><th>Brand</th>
          <th class="n">Price</th><th class="n">Tomorrow</th><th>Reported</th>
        </tr></thead>
        <tbody>${rows.map((s, i) => {
          const p = s.p[product];
          const t = s.t?.[product];
          const delta = t != null ? t - p : null;
          return `<tr class="clickable" data-key="${esc(`${s.n}|${s.s}|${s.st}`)}">
            <td class="n rank">${i + 1}</td>
            <td><span class="truncate" style="display:block;max-width:22ch">${esc(s.n)}</span></td>
            <td>${esc(s.s)}</td>
            <td>${esc(s.b || '—')}</td>
            <td class="n"><span data-tip="${escTip(`${cents(p)} cents per litre\n${deltaLabel(p - lo)} vs the cheapest station`)}" style="color:${priceColour(p, lo, hi)};font-weight:650">${cents(p)}</span></td>
            <td class="n">${t != null ? `<span data-tip="${escTip(`Tomorrow: ${cents(t)}c\n${deltaLabel(delta!)} on today`)}" style="color:${delta! > 0 ? 'var(--status-bad)' : delta! < 0 ? 'var(--status-good)' : 'inherit'}">${cents(t)} <small>${deltaLabel(delta!)}</small></span>` : '—'}</td>
            <td>${esc(shortDate(s.d))}</td>
          </tr>`;
        }).join('')}</tbody>
      </table>
    </div>
    <p class="brand-sub" style="margin-top:var(--space-sm)">
      Showing ${rows.length} of ${sites.length} matching stations${total !== sites.length ? ` (${total} currently reporting in this state)` : ''}, cheapest first. Click a row for that station's detail.
      ${stale ? `${stale} station${stale === 1 ? '' : 's'} last reported more than a fortnight ago and are left out here — a price that old is not an offer. They still appear under Suburbs, which covers four months.` : ''}
    </p>
  `;
  host.querySelectorAll('tr.clickable').forEach((tr) => {
    tr.addEventListener('click', () => {
      const key = (tr as HTMLElement).dataset.key ?? '';
      const site = sites.find((s) => `${s.n}|${s.s}|${s.st}` === key);
      if (site) openStation(site);
    });
  });
}
